import { useEffect, useState } from "react";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import BadgeIcon from "@mui/icons-material/Badge";
import PeopleIcon from "@mui/icons-material/People";
import { fetchQuery } from "../../utils/commonComponent/fetched";
import PolicyAgreementModal from "./PolicyAgreementModal";
import moment from "moment/moment";

// -------------------- StatCard Component --------------------
function StatCard({ title, icon, today, overall, simple = false }) {
  return (
    <div className="bg-white rounded-lg shadow p-5 space-y-4 border">
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-gray-100 rounded-full">{icon}</div>
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
      </div>

      {simple ? (
        <div className="text-center text-3xl font-bold text-gray-900">{overall}</div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 p-3 rounded text-center border border-blue-100">
            <p className="text-sm text-blue-800">Today</p>
            <p className="text-xl font-bold text-blue-900">{today}</p>
          </div>
          <div className="bg-gray-100 p-3 rounded text-center border border-gray-200">
            <p className="text-sm text-gray-700">Overall</p>
            <p className="text-xl font-bold text-gray-900">{overall}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- Dashboard Component --------------------
export default function Dashboard() {
  const userType = localStorage.getItem("type") || "";
  const userID = localStorage.getItem("id") || "";
  const policyStatus = localStorage.getItem("policyStatus") || "";
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [stats, setStats] = useState([]);
  const [inspectorDetails, setInspectorDetails] = useState({
    inspectorName: "",
    userSign: localStorage.getItem("userSign") || "",
    policyDate: moment(localStorage.getItem("policyDate")).isValid() ? moment(localStorage.getItem("policyDate")).format("DD-MM-YYYY") : moment().format("DD-MM-YYYY"),
  });

  useEffect(() => {
    const fetchStats = async () => {
      
      if (userType === "2") {
        // 🔹 Fetch Inspector Name
        const inspectorResult = await fetchQuery(
          `SELECT u.*, emp.name, emp.state FROM users u LEFT JOIN employees emp ON u.employeeId = emp.id WHERE u.id = ${userID}`
        ).catch((err) => console.log(err));
        setInspectorDetails((prev) => ({
          ...prev,
          inspectorName: inspectorResult[0]?.name || "",
        }));

        // Inspector view
        const [
          assignedJobs = [{ overall: 0 }],
          pendingJobs = [{ overall: 0 }],
          completedJobs = [{ overall: 0 }],
        ] = await Promise.all([
          fetchQuery(`SELECT COUNT(*) AS overall FROM jobRegisterInspection WHERE assigneuserID in (${userID})`).catch(() => [{ overall: 0 }]),
          fetchQuery(`SELECT COUNT(*) AS overall FROM IR JOIN jobRegisterInspection ON IR.jobRegisterInspectionId = jobRegisterInspection.id WHERE jobRegisterInspection.assigneuserID = ${userID} AND IR.issueDate IS NULL`).catch(() => [{ overall: 0 }]),
          fetchQuery(`SELECT COUNT(*) AS overall FROM IR JOIN jobRegisterInspection ON IR.jobRegisterInspectionId = jobRegisterInspection.id WHERE jobRegisterInspection.assigneuserID = ${userID} AND IR.issueDate IS NOT NULL`).catch(() => [{ overall: 0 }])

        ]);

        setStats([
          {
            title: "Inspector Assigned Jobs",
            icon: <AssignmentIcon className="text-blue-600" fontSize="large" />,
            overall: assignedJobs[0].overall || 0,
            simple: true,
          },
          {
            title: "Pending Jobs",
            icon: <PendingActionsIcon className="text-yellow-600" fontSize="large" />,
            overall: pendingJobs[0].overall || 0,
            simple: true,
          },
          {
            title: "Completed Jobs",
            icon: <CheckCircleIcon className="text-green-600" fontSize="large" />,
            overall: completedJobs[0].overall || 0,
            simple: true,
          },
        ]);
      } else {
        // Admin/Other view
        const queryMap = [
          {
            key: "Clients",
            overallQuery: `SELECT COUNT(*) AS overall FROM clients`,
            todayQuery: `SELECT COUNT(*) AS today FROM clients WHERE DATE(lastModified) = CURDATE()`,
            icon: <PeopleIcon className="text-blue-600" fontSize="large" />,
          },
          {
            key: "Employees",
            overallQuery: `SELECT COUNT(*) AS overall FROM employees`,
            todayQuery: null,
            icon: <BadgeIcon className="text-purple-600" fontSize="large" />,
          },
          {
            key: "Inspection Services",
            overallQuery: `SELECT COUNT(*) AS overall FROM serviceRequest`,
            todayQuery: null,
            icon: <AssignmentIcon className="text-yellow-600" fontSize="large" />,
          },
          {
            key: "Quotation",
            overallQuery: `SELECT COUNT(*) AS overall FROM quotation`,
            todayQuery: `SELECT COUNT(*) AS today FROM quotation WHERE DATE(lastModified) = CURDATE()`,
            icon: <AssignmentIcon className="text-yellow-600" fontSize="large" />,
          },
          {
            key: "PO",
            overallQuery: `SELECT COUNT(*) AS overall FROM poAcceptance`,
            todayQuery: `SELECT COUNT(*) AS today FROM poAcceptance WHERE DATE(lastModified) = CURDATE()`,
            icon: <AssignmentIcon className="text-yellow-600" fontSize="large" />,
          },
        ];

        const results = await Promise.all(
          queryMap.flatMap((q) => [
            fetchQuery(q.overallQuery).catch(() => [{ overall: 0 }]),
            q.todayQuery ? fetchQuery(q.todayQuery).catch(() => [{ today: 0 }]) : Promise.resolve([{ today: 0 }]),
          ])
        );

        const updatedStats = queryMap.map((q, i) => ({
          title: q.key,
          icon: q.icon,
          overall: results[i * 2]?.[0]?.overall || 0,
          today: results[i * 2 + 1]?.[0]?.today || 0,
          simple: false,
        }));

        setStats(updatedStats);
      }
    };

    fetchStats();
  }, [userType, userID]);

  useEffect(() => {
    if (userType == "2" && policyStatus == "0") {
      setShowPolicyModal(true);
    }
  }, [userType, policyStatus]);

  return (
    <div className="p-6 min-h-screen">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Dashboard</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((item) => (
          <StatCard key={item.title} {...item} />
        ))}
      </div>

      <PolicyAgreementModal inspectorDetails={inspectorDetails} open={showPolicyModal} onClose={() => setShowPolicyModal(false)} />
    </div>
  );
}
